import React, { useState, useEffect, useRef } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  array, bool, func, object, string,
} from 'prop-types'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import Header from './Components/Header'
import Images from './Components/Images'
import ModelInfo from './Components/ModelInfo'

function ModelGallery({
  onAddToCart = null,
  onCloseGallery,
  onMount = null,
  onAddToWishlist = null,
  onRemoveFromWishlist = null,
  priceInfo,
  title,
  id,
  isWishListActionLoading = false,
  isCartActionLoading = false,
  tags = EMPTY_ARRAY,
  currentSelectedFileTypes,
  setSelectedFilters,
  trackFilterChange,
  onRemoveFromCart,
  cartItemIds = EMPTY_ARRAY,
  wishListItems = EMPTY_ARRAY,
  isPurchased = false,
  showCgtVerification,
  itemAttributes,
  modelUrl,
}) {
  const [isLoading, setIsLoading] = useState(true)
  const [isInitialRender, setIsInitialRender] = useState(true)
  const activeBreakpoint = useBreakpoint()
  const [activeIndex, setActiveIndex] = useState(0)
  const [showSwipeButton, setShowSwipeButton] = useState(true)
  const shouldShowOnlyFullscreen = ['xs', 'sm'].includes(activeBreakpoint)
  const [fullScreenGallery, setFullScreenGallery] = useState(shouldShowOnlyFullscreen)
  const isTiny = activeBreakpoint === 'xs'
  const previewRef = useRef(null)
  const [galleryImageUrls, setGalleryImageUrls] = useState([])
  const [userInfo, setUserInfo] = useState({})
  const { username, userPicture, userRating } = userInfo || {}
  const [modelInfo, setModelInfo] = useState({})
  const {
    modelRating,
    modelRatingCount,
    reviewCount,
    commentCount,
    likeCount,
    viewCount,
    types,
    polygons,
    vertices,
    textures,
    materials,
    uvw_mapping: uvwMapping,
    plugins_used: pluginsUsed,
    isCgtStandardThresholdReached,
  } = modelInfo
  const [cgtVerification, setCgtVerification] = useState({})
  const isOnWishlist = wishListItems.includes(Number(id))
  const totalImageCount = galleryImageUrls.length
  const cartItemId = (() => {
    const cartItem = cartItemIds.find(({ itemId }) => Number(itemId) === Number(id))
    return cartItem?.cartId
  })()

  useEffect(() => {
    if (onMount) {
      onMount(previewRef)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    const getModelData = async () => {
      try {
        const model = await window.fetchJSON(`/listing_page/items/${id}`)

        if (model && model.data && isMounted) {
          const { attributes } = model.data
          setGalleryImageUrls(attributes.galleryImageUrls)
          setUserInfo(attributes.userInfo)
          setModelInfo(attributes.modelInfo)
          setCgtVerification(attributes.cgtVerification)
          setIsLoading(false)
        }
      } catch (error) {
        // to do: add error handling
        // once Sentry is available in listing page
      }
    }

    if (id) {
      getModelData()
    }

    return () => {
      isMounted = false
    }
  }, [id])

  const handleActiveSlide = ({ realIndex }) => {
    if (isInitialRender) {
      setIsInitialRender(false)
      return
    }

    setActiveIndex(realIndex)

    if (showSwipeButton) {
      setShowSwipeButton(false)
    }
  }

  function triggerFullScreenGallery() {
    return fullScreenGallery ? closeFullscreenGallery() : openFullscreenGallery()
  }

  function openFullscreenGallery() {
    setFullScreenGallery(true)
    document.body.classList.add('disable-page-scroll')
  }

  function closeFullscreenGallery() {
    setFullScreenGallery(false)
    document.body.classList.remove('disable-page-scroll')

    if (shouldShowOnlyFullscreen) {
      onCloseGallery()
    }

    return null
  }

  function handleKeyDown(event) {
    // ESC closes fullscreen gallery
    if (event.keyCode === 27) {
      closeFullscreenGallery()
    }

    return null
  }

  function renderModelInfo() {
    return (
      <ModelInfo
        isLoading={isLoading}
        id={id}
        title={title}
        modelRating={modelRating}
        modelRatingCount={modelRatingCount}
        commentCount={commentCount}
        likeCount={likeCount}
        modelUrl={modelUrl}
        reviewCount={reviewCount}
        username={username}
        userPicture={userPicture}
        userRating={userRating}
        viewCount={viewCount}
        onAddToWishlist={onAddToWishlist}
        onRemoveFromWishlist={onRemoveFromWishlist}
        isOnWishlist={isOnWishlist}
        isWishListActionLoading={isWishListActionLoading}
        onAddToCart={onAddToCart}
        onRemoveFromCart={onRemoveFromCart}
        isOnCart={!!cartItemId}
        isCartActionLoading={isCartActionLoading}
        priceInfo={priceInfo}
        currentSelectedFileTypes={currentSelectedFileTypes}
        setSelectedFilters={setSelectedFilters}
        trackFilterChange={trackFilterChange}
        tags={tags}
        types={types}
        polygons={polygons}
        vertices={vertices}
        textures={textures}
        materials={materials}
        uvwMapping={uvwMapping}
        pluginsUsed={pluginsUsed}
        cgtVerification={cgtVerification}
        fullScreenGallery={fullScreenGallery}
        hideInfo={isTiny}
        isPurchased={isPurchased}
        isCgtStandardThresholdReached={isCgtStandardThresholdReached}
        showCgtVerification={showCgtVerification}
        itemAttributes={itemAttributes}
      />
    )
  }

  return (
    <>
      {fullScreenGallery && <div className="cgt-modal--background" />}
      <div
        className={classnames(
          'cgt-model-gallery-new cgt-card cgt-effect-card-border-disabled',
          fullScreenGallery ? 'cgt-model-gallery-new--full-screen' : 'cgt-model-gallery-new--auto',
        )}
        ref={previewRef}
      >
        <div className="cgt-model-gallery-new__main-content">
          <div className="cgt-model-gallery-new__actions">
            <Header
              onCloseGallery={onCloseGallery}
              activeIndex={activeIndex}
              totalCount={totalImageCount}
              showSwipeButton={showSwipeButton}
              fullScreenGallery={fullScreenGallery}
              triggerFullScreenGallery={triggerFullScreenGallery}
            />
            {fullScreenGallery && (
              renderModelInfo()
            )}
          </div>
          <Images
            isLoading={isLoading}
            galleryImageUrls={galleryImageUrls}
            onActiveIndexChange={handleActiveSlide}
            fullScreenGallery={fullScreenGallery}
            activeIndex={activeIndex}
            totalCount={totalImageCount}
          />
        </div>
        {!fullScreenGallery && (
          renderModelInfo()
        )}
      </div>
    </>
  )
}

ModelGallery.propTypes = {
  onAddToCart: func,
  onCloseGallery: func.isRequired,
  onMount: func,
  onAddToWishlist: func,
  onRemoveFromWishlist: func,
  priceInfo: object.isRequired,
  title: string.isRequired,
  id: string.isRequired,
  isWishListActionLoading: bool,
  isCartActionLoading: bool,
  tags: array,
  currentSelectedFileTypes: array.isRequired,
  setSelectedFilters: func.isRequired,
  trackFilterChange: func.isRequired,
  onRemoveFromCart: func.isRequired,
  cartItemIds: array,
  wishListItems: array,
  isPurchased: bool,
  showCgtVerification: bool.isRequired,
  itemAttributes: object.isRequired,
  modelUrl: string.isRequired,
}

export default ModelGallery
